import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, X, ArrowUpRight, Calendar, Clock, User } from "lucide-react";
import SEO from "@/components/common/SEO";
import ShareMenu from "@/components/common/ShareMenu";
import {
    GLOSARIO_INTRO,
    GLOSARIO_OUTRO,
} from "@/data/staticContent";
import { api, SITE_URL } from "@/lib/api";

export default function Glosario() {
    const [query, setQuery] = useState("");
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/glosario?limit=1000")
            .then(({ data }) => setTerms(data))
            .catch(() => setTerms([]))
            .finally(() => setLoading(false));
    }, []);

    // Group by letter, preserve original order
    const grouped = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? terms.filter(
                  (g) =>
                      g.term.toLowerCase().includes(q) ||
                      g.definition.toLowerCase().includes(q),
              )
            : terms;
        const map = new Map();
        for (const item of filtered) {
            const letter = item.letter || item.term[0].toUpperCase();
            if (!map.has(letter)) map.set(letter, []);
            map.get(letter).push(item);
        }
        return Array.from(map.entries()).map(([letter, items]) => ({
            letter,
            items,
        }));
    }, [query, terms]);

    const totalCount = grouped.reduce((acc, g) => acc + g.items.length, 0);
    const allLetters = useMemo(() => {
        const set = new Set();
        for (const g of terms) set.add(g.letter || g.term[0].toUpperCase());
        return Array.from(set);
    }, [terms]);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        name: "Glosario del Adicto",
        description:
            "Definiciones íntimas y críticas sobre adicción, autoengaño y recuperación que la RAE nunca se atrevió a escribir.",
        url: `${SITE_URL}/glosario`,
        hasDefinedTerm: terms.map((g) => ({
            "@type": "DefinedTerm",
            name: g.term,
            description: g.definition,
            inDefinedTermSet: `${SITE_URL}/glosario`,
        })),
    };

    return (
        <>
            <SEO
                title="Glosario del Adicto · Diccionario interno"
                description="Definiciones que la RAE nunca se atrevió a escribir. 97 entradas sobre adicción, autoengaño, craving, recaída, sobriedad, ketamina, pasta base y más, contadas sin maquillaje por Nelson González (@NiTanAdicto)."
                path="/glosario"
                keywords="glosario adicto, diccionario adicción, autoengaño, craving, recaída, sobriedad, dopamina, pasta base, ketamina, cocaína, NiTanAdicto"
                jsonLd={jsonLd}
            />

            {/* HERO con preámbulo */}
            <section
                className="grain relative py-20 md:py-28 border-b border-[var(--line)]"
                data-testid="glosario-hero"
            >
                <div className="container-x">
                    <div className="flex items-center gap-3 mb-6 flex-wrap font-mono text-[10px] uppercase tracking-[0.3em]">
                        <span className="text-[var(--danger)]">
                            {GLOSARIO_INTRO.eyebrow}
                        </span>
                        <span className="dotted-div flex-1 min-w-[40px]" />
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            {GLOSARIO_INTRO.date}
                        </span>
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            {GLOSARIO_INTRO.readingTime}
                        </span>
                        <span className="text-[var(--ink-muted)] inline-flex items-center gap-1.5">
                            <User className="w-3 h-3" />
                            @NiTanAdicto
                        </span>
                    </div>

                    <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[7rem] leading-[0.85] tracking-tight mb-6">
                        Glosario del{" "}
                        <span className="text-[var(--danger)]">Adicto</span>
                    </h1>

                    <p className="font-hand text-2xl md:text-3xl text-[var(--warning)] -rotate-1 inline-block mb-8">
                        {GLOSARIO_INTRO.subtitle}
                    </p>

                    {/* Preámbulo */}
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 mt-10">
                        <div className="lg:col-span-7 space-y-4">
                            {GLOSARIO_INTRO.paragraphs.map((p, i) => (
                                <p
                                    key={i}
                                    className={`font-mono leading-[1.75] ${i === 2 ? "text-[var(--paper-2)] text-lg" : "text-[var(--paper)] text-base"}`}
                                    data-testid={`glosario-intro-${i}`}
                                >
                                    {p}
                                </p>
                            ))}
                        </div>

                        {/* Stats + search */}
                        <div className="lg:col-span-5 space-y-6 self-end">
                            <div className="flex gap-8">
                                <div>
                                    <p className="font-display text-5xl md:text-6xl text-[var(--danger)] leading-none">
                                        {terms.length}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-2">
                                        Entradas archivadas
                                    </p>
                                </div>
                                <div>
                                    <p className="font-display text-5xl md:text-6xl text-[var(--warning)] leading-none">
                                        {allLetters.length}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-2">
                                        Letras del idioma roto
                                    </p>
                                </div>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-dim)]" />
                                <input
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Buscar término o definición…"
                                    className="input-dossier pl-7 pr-8"
                                    data-testid="glosario-search"
                                />
                                {query && (
                                    <button
                                        onClick={() => setQuery("")}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--danger)]"
                                        aria-label="Limpiar búsqueda"
                                        data-testid="glosario-search-clear"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sticky alphabet index */}
            <nav
                className="sticky top-[88px] z-30 bg-[#0d0d0d]/95 backdrop-blur border-b border-[var(--line)]"
                aria-label="Índice alfabético"
                data-testid="alphabet-nav"
            >
                <div className="container-x py-3 overflow-x-auto">
                    <ul className="flex gap-1 min-w-max items-center">
                        <li className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] mr-2">
                            Índice ·
                        </li>
                        {allLetters.map((letter) => {
                            const has = grouped.find(
                                (g) => g.letter === letter,
                            );
                            const count = has ? has.items.length : 0;
                            const enabled = count > 0;
                            return (
                                <li key={letter}>
                                    <a
                                        href={`#letter-${letter}`}
                                        onClick={(e) => {
                                            if (!enabled) {
                                                e.preventDefault();
                                                return;
                                            }
                                            e.preventDefault();
                                            document
                                                .getElementById(
                                                    `letter-${letter}`,
                                                )
                                                ?.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "start",
                                                });
                                        }}
                                        className={`font-display text-base md:text-lg px-3 py-1 inline-flex items-center gap-1.5 border ${
                                            enabled
                                                ? "border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                                : "border-transparent text-[var(--ink-dim)] cursor-not-allowed opacity-40"
                                        }`}
                                        data-testid={`letter-link-${letter}`}
                                        aria-disabled={!enabled}
                                    >
                                        {letter}
                                        {enabled && (
                                            <span className="font-mono text-[9px] text-[var(--ink-muted)]">
                                                {count}
                                            </span>
                                        )}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </nav>

            {/* Sections per letter */}
            {grouped.length === 0 && (
                <section className="py-32 text-center">
                    <p className="font-mono text-sm text-[var(--ink-muted)] mb-4">
                        Sin coincidencias para "{query}".
                    </p>
                    <button
                        onClick={() => setQuery("")}
                        className="btn-brutal btn-brutal-ghost"
                    >
                        Limpiar búsqueda
                    </button>
                </section>
            )}

            {grouped.map((group) => (
                <section
                    key={group.letter}
                    id={`letter-${group.letter}`}
                    className="py-14 md:py-20 border-b border-[var(--line)] scroll-mt-32"
                    data-testid={`letter-section-${group.letter}`}
                >
                    <div className="container-x">
                        {/* Letter header */}
                        <header className="mb-10 grid lg:grid-cols-12 gap-6 items-end">
                            <div className="lg:col-span-3">
                                <p className="font-display text-[7rem] md:text-[10rem] lg:text-[12rem] leading-[0.8] text-[var(--danger)] opacity-70">
                                    {group.letter}
                                </p>
                            </div>
                            <div className="lg:col-span-9 pb-3">
                                <p className="eyebrow mb-2">
                                    {group.items.length}{" "}
                                    {group.items.length === 1
                                        ? "entrada"
                                        : "entradas"}
                                </p>
                                <h2 className="font-display text-3xl md:text-4xl text-[var(--paper-2)]">
                                    {group.letter === "W-Z"
                                        ? "Las últimas letras"
                                        : `Letra ${group.letter}`}
                                </h2>
                            </div>
                        </header>

                        {/* Definitions */}
                        <dl className="grid md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
                            {group.items.map((g, i) => {
                                const termSlug = g.term
                                    .toLowerCase()
                                    .normalize("NFD")
                                    .replace(/[\u0300-\u036f]/g, "")
                                    .replace(/[^a-z0-9]+/g, "-")
                                    .replace(/^-+|-+$/g, "");
                                return (
                                    <div
                                        key={`${group.letter}-${i}`}
                                        className="bg-[#0d0d0d] p-6 md:p-7 hover:bg-[#1a1a1a] transition-colors group relative"
                                        data-testid={`term-${termSlug}`}
                                        id={`term-${termSlug}`}
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)]">
                                                {group.letter} ·{" "}
                                                {String(i + 1).padStart(2, "0")}{" "}
                                                /{" "}
                                                {String(
                                                    group.items.length,
                                                ).padStart(2, "0")}
                                            </p>
                                            <div className="opacity-50 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                                <ShareMenu
                                                    title={g.term}
                                                    text={g.definition}
                                                    path={`/glosario#term-${termSlug}`}
                                                    source="Glosario del Adicto"
                                                    testid={`share-term-${termSlug}`}
                                                    hashtags={[
                                                        "nitanadicto",
                                                        "glosariodeladicto",
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                        <dt className="font-display text-2xl md:text-3xl mb-3 text-[var(--warning)] leading-tight">
                                            {g.term}
                                        </dt>
                                        <dd className="font-mono text-sm md:text-base text-[var(--paper)] leading-relaxed">
                                            {g.definition}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>
                    </div>
                </section>
            ))}

            {/* Outro */}
            <section
                className="py-20 md:py-28 grain"
                data-testid="glosario-outro"
            >
                <div className="container-x max-w-3xl">
                    <p className="eyebrow mb-6">Epílogo</p>
                    <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">
                        {GLOSARIO_OUTRO.title}
                    </h2>
                    <p className="font-mono text-lg md:text-xl text-[var(--paper)] leading-relaxed mb-10">
                        {GLOSARIO_OUTRO.body}
                    </p>

                    <ul className="space-y-5 mb-12">
                        {GLOSARIO_OUTRO.examples.map((ex, i) => (
                            <li
                                key={i}
                                className="border-l-2 border-[var(--danger)] pl-5"
                                data-testid={`outro-example-${i}`}
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-1">
                                    {String(i + 1).padStart(2, "0")} · Re-significado
                                </p>
                                <p className="font-display text-xl md:text-2xl text-[var(--paper-2)] leading-snug">
                                    <strong className="text-[var(--warning)]">
                                        "{ex.word}"
                                    </strong>{" "}
                                    significa{" "}
                                    <em className="text-[var(--paper-2)]">
                                        {ex.meaning}
                                    </em>
                                </p>
                            </li>
                        ))}
                    </ul>

                    <div className="border-t border-dashed border-[var(--ink-dim)] pt-10">
                        <p className="font-display italic text-2xl md:text-3xl text-[var(--paper-2)] leading-tight mb-8">
                            {GLOSARIO_OUTRO.closing}
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <Link
                                to="/antipoesia"
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="outro-antipoesia"
                            >
                                Leer antipoesía
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/salida"
                                className="btn-brutal btn-brutal-danger"
                                data-testid="outro-salida"
                            >
                                Salida de emergencia
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
